import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { Switch } from '../../components/ui/Switch.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import {
  type Statement,
  type StatementCar,
  ringgit,
  statementRows,
  statementToCsv,
} from './salesCsv.js';

/**
 * 342 (#933, #916): a supplier's sales statement, in the reference sheet's layout. Platform staff (`read
 * SalesStatement` anywhere) choose a supplier; an organisation's own reader sees theirs (the server decides which, from
 * the permission). The figures are the server's; this page only lays them out, and the CSV is built from the same
 * rows. Adjustment lines (penalty, compensation, self booking) are kept here by staff who may manage them.
 *
 * 377 (#1233): money is the supplier's — there is no host view or host split (369's "By host" is gone). A host with
 * "Can see sales" reads their own supplier's statement here, through the same permission as the supplier's staff.
 */

const amount = (minor: number) =>
  (minor / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** `2026-06-01`, `2026-06-07` → "1–7 Jun 2026"; across months or years, both ends in full. */
function periodLabel(from: string, to: string): string {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  const day = (y: number, m: number, d: number) => `${d} ${MONTH[m - 1]} ${y}`;
  if (fy === ty && fm === tm) return `${fd}–${td} ${MONTH[tm - 1]} ${ty}`;
  return `${day(fy, fm, fd)} – ${day(ty, tm, td)}`;
}

const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Today's date in Malaysia, `YYYY-MM-DD`, whatever the device's zone. */
const mytToday = () => new Date(Date.now() + MYT_OFFSET_MS).toISOString().slice(0, 10);
const shiftDay = (day: string, days: number) =>
  new Date(new Date(`${day}T00:00:00.000Z`).getTime() + days * DAY_MS).toISOString().slice(0, 10);

type Preset = 'week' | 'month' | 'last-month';
function presetRange(p: Preset): { from: string; to: string } {
  const today = mytToday();
  if (p === 'week') {
    const dow = (new Date(`${today}T00:00:00.000Z`).getUTCDay() + 6) % 7; // Monday = 0
    const from = shiftDay(today, -dow);
    return { from, to: shiftDay(from, 6) };
  }
  if (p === 'month') return { from: `${today.slice(0, 7)}-01`, to: today };
  const firstThisMonth = `${today.slice(0, 7)}-01`;
  const to = shiftDay(firstThisMonth, -1);
  return { from: `${to.slice(0, 7)}-01`, to };
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** 424: the period in the address (the dashboard's "See full statement"), when it is two real days in order. */
function addressPeriod(params: URLSearchParams): { from: string; to: string } | null {
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const real = (d: string) =>
    DAY.test(d) && new Date(`${d}T00:00:00.000Z`).toISOString().slice(0, 10) === d;
  return real(from) && real(to) && from <= to ? { from, to } : null;
}

const KIND_LABEL: Record<string, string> = {
  penalty: 'Penalty',
  compensation: 'Compensation',
  self_booking: 'Self booking',
};

const formatNet = (minor: number) =>
  `RM ${(minor / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function errorText(error: unknown): string {
  const e = error as { status?: number; message?: string } | null;
  if (e?.status === 403) return 'You may not see this supplier’s statement.';
  return e?.message ?? 'The statement could not be loaded.';
}

type LineDraft = { kind: string; on: string; amount: string; note: string };

function LineForm({
  carId,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
}: {
  carId: string;
  initial: LineDraft;
  submitLabel: string;
  onSubmit: (d: { kind: string; on: string; amount_minor: number; note: string }) => void;
  onCancel?: () => void;
  busy: boolean;
}) {
  const [d, setD] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const prefix = `${submitLabel === 'Add line' ? 'new' : 'edit'}-${carId}`;
  return (
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-5 sm:items-end">
      <FormField label="Kind" name={`${prefix}-kind`}>
        <Select
          id={`${prefix}-kind`}
          value={d.kind}
          onChange={(e) => setD({ ...d, kind: e.target.value })}
        >
          {/* 371 (FR-005): new penalties go through the penalty tracker; an old penalty line can still be edited. */}
          {initial.kind === 'penalty' && <option value="penalty">Penalty</option>}
          <option value="compensation">Compensation</option>
          <option value="self_booking">Self booking</option>
        </Select>
      </FormField>
      <FormField label="Date" name={`${prefix}-on`}>
        <Input
          id={`${prefix}-on`}
          type="date"
          value={d.on}
          onChange={(e) => setD({ ...d, on: e.target.value })}
        />
      </FormField>
      <FormField label="Amount (RM)" name={`${prefix}-amount`}>
        <Input
          id={`${prefix}-amount`}
          inputMode="decimal"
          value={d.amount}
          onChange={(e) => setD({ ...d, amount: e.target.value })}
        />
      </FormField>
      <FormField label="Note" name={`${prefix}-note`}>
        <Input
          id={`${prefix}-note`}
          maxLength={300}
          value={d.note}
          onChange={(e) => setD({ ...d, note: e.target.value })}
        />
      </FormField>
      <div className="flex gap-2">
        <Button
          type="button"
          loading={busy}
          onClick={() => {
            const amount = Math.round(Number.parseFloat(d.amount) * 100);
            if (!Number.isFinite(amount) || amount <= 0)
              return setError('Enter an amount above 0.');
            if (!/^\d{4}-\d{2}-\d{2}$/.test(d.on)) return setError('Choose a date.');
            setError(null);
            onSubmit({ kind: d.kind, on: d.on, amount_minor: amount, note: d.note.trim() });
          }}
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-600 sm:col-span-5">
          {error}
        </p>
      )}
    </div>
  );
}

function CarLines({
  car,
  period,
  manages,
  onChanged,
}: {
  car: StatementCar;
  period: { from: string; to: string };
  manages: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const edit = useMutation({
    mutationFn: ({
      id,
      ...d
    }: { id: string; kind: string; on: string; amount_minor: number; note: string }) =>
      api.patch(`/sales/adjustments/${id}`, d),
    onSuccess: () => {
      setEditing(null);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/sales/adjustments/${id}`),
    onSuccess: onChanged,
  });
  const failed = edit.error ?? remove.error;
  return (
    <Card data-testid={`car-lines-${car.car_id}`}>
      <h3 className="mb-2 text-sm font-semibold text-ink">
        {car.plate_number ?? car.label} <span className="font-normal text-steel">{car.label}</span>
      </h3>
      {car.settings_missing.length > 0 && (
        <p className="mb-2 text-xs text-amber-700">
          Not set on the car:{' '}
          {car.settings_missing
            .map((m) => (m === 'monthly_cost' ? 'monthly cost' : 'commission rate'))
            .join(' and ')}{' '}
          (counted as 0).
        </p>
      )}
      <p className="text-xs font-medium uppercase text-steel">Bookings counted</p>
      {car.bookings.length === 0 ? (
        <p className="text-sm text-steel">None in this period.</p>
      ) : (
        <ul className="mb-2 text-sm text-ink">
          {car.bookings.map((b) => (
            <li key={b.id}>
              {new Date(b.pickup_at).toLocaleDateString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' })}{' '}
              · RM {ringgit(b.amount_minor)} · {b.status.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs font-medium uppercase text-steel">Adjustment lines</p>
      {car.adjustments.length === 0 ? (
        <p className="text-sm text-steel">None in this period.</p>
      ) : (
        <ul className="text-sm text-ink">
          {car.adjustments.map((a) =>
            editing === a.id ? (
              <li key={a.id}>
                <LineForm
                  carId={car.car_id}
                  initial={{
                    kind: a.kind,
                    on: a.on,
                    amount: ringgit(a.amount_minor),
                    note: a.note,
                  }}
                  submitLabel="Save line"
                  busy={edit.isPending}
                  onSubmit={(d) => edit.mutate({ id: a.id, ...d })}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li key={a.id} className="flex items-center gap-3">
                <span>
                  {a.on} · {KIND_LABEL[a.kind] ?? a.kind} · RM {ringgit(a.amount_minor)}
                  {a.note ? ` · ${a.note}` : ''}
                </span>
                {/* 371: a tracker penalty is edited on the Penalties page, never as a line. */}
                {a.penalty_id ? (
                  <Link className="text-xs text-primary underline" to="/penalties">
                    Penalties
                  </Link>
                ) : (
                  manages && (
                    <>
                      <button
                        type="button"
                        className="text-xs text-primary underline"
                        onClick={() => setEditing(a.id)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-xs text-red-600 underline"
                        onClick={() => remove.mutate(a.id)}
                      >
                        Remove
                      </button>
                    </>
                  )
                )}
              </li>
            ),
          )}
        </ul>
      )}
      {failed && (
        <p className="mt-2 text-sm text-red-600">
          {(failed as { message?: string }).message ?? 'Save failed.'}
        </p>
      )}
    </Card>
  );
}

/** 370: the number of Malaysia days from `from` to `to`, both counted. */
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;

const PRESETS: Array<[Preset, string]> = [
  ['week', 'This week'],
  ['month', 'This month'],
  ['last-month', 'Last month'],
];

/** 370: the sheet's "Payment Status": settled or not, with a note, for exactly this statement. */
function PaymentCard({
  statement,
  supplierId,
  period,
  manages,
  onSaved,
}: {
  statement: Statement;
  supplierId: string;
  period: { from: string; to: string };
  manages: boolean;
  onSaved: () => void;
}) {
  const recorded = statement.payment ?? null;
  const [paid, setPaid] = useState(recorded?.paid ?? false);
  const [note, setNote] = useState(recorded?.note ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.put('/sales/statement/payment', {
        supplier_id: supplierId,
        from: period.from,
        to: period.to,
        paid,
        note: note.trim(),
      }),
    onSuccess: onSaved,
  });
  const changed = paid !== (recorded?.paid ?? false) || note.trim() !== (recorded?.note ?? '');
  const when = recorded?.at
    ? new Date(recorded.at).toLocaleDateString('en-MY', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kuala_Lumpur',
      })
    : '';
  return (
    <Card data-testid="payment-status">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-steel">
            Payment Status
          </span>
          {manages ? (
            <Switch checked={paid} onChange={() => setPaid((p) => !p)} ariaLabel="Paid" />
          ) : null}
          <b className={paid ? 'text-green-700' : 'text-steel'}>
            {paid ? 'Paid' : 'Not marked as paid'}
          </b>
          {recorded?.by_name ? (
            <span className="text-xs text-steel">
              by {recorded.by_name}
              {when ? ` · ${when}` : ''}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {manages ? (
            <>
              <Input
                aria-label="Payment note"
                className="min-w-72"
                maxLength={300}
                placeholder="Note, e.g. Still owe RM83.12"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                type="button"
                disabled={!changed || save.isPending}
                onClick={() => save.mutate()}
              >
                Save
              </Button>
            </>
          ) : recorded?.note ? (
            <span className="text-sm text-ink">{recorded.note}</span>
          ) : null}
        </div>
      </div>
      {save.error ? (
        <p className="mt-2 text-sm text-red-600">
          {(save.error as { message?: string }).message ?? 'Save failed.'}
        </p>
      ) : null}
    </Card>
  );
}

/** 370: one "Add line" for the page; the car is chosen here. */
function AddLineDialog({
  open,
  onClose,
  cars,
  period,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  cars: StatementCar[];
  period: { from: string; to: string };
  onAdded: () => void;
}) {
  const [carId, setCarId] = useState(cars[0]?.car_id ?? '');
  const add = useMutation({
    mutationFn: (d: { kind: string; on: string; amount_minor: number; note: string }) =>
      api.post('/sales/adjustments', { car_id: carId, ...d }),
    onSuccess: () => {
      onAdded();
      onClose();
    },
  });
  return (
    <Modal open={open} onClose={onClose} title="Add a line" size="lg">
      <div className="flex flex-col gap-3">
        <FormField label="Car" name="add-line-car">
          <Select id="add-line-car" value={carId} onChange={(e) => setCarId(e.target.value)}>
            {cars.map((c) => (
              <option key={c.car_id} value={c.car_id}>
                {c.plate_number ?? c.label} · {c.label}
              </option>
            ))}
          </Select>
        </FormField>
        <LineForm
          key={`${carId}-${period.from}-${period.to}`}
          carId={carId}
          initial={{ kind: 'compensation', on: period.to, amount: '', note: '' }}
          submitLabel="Add line"
          busy={add.isPending}
          onSubmit={(d) => add.mutate(d)}
          onCancel={onClose}
        />
        {add.error ? (
          <p className="text-sm text-red-600">
            {(add.error as { message?: string }).message ?? 'Save failed.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export function SalesStatementPage() {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const everyHost = holdsAnywhere(ability as never, 'read', 'SalesStatement');
  const manages = holdsAnywhere(ability as never, 'create', 'SalesAdjustment');
  const [params] = useSearchParams();
  const [preset, setPreset] = useState<Preset | null>(() =>
    addressPeriod(params) ? null : 'month',
  );
  const [period, setPeriod] = useState(() => addressPeriod(params) ?? presetRange('month'));
  const [supplierId, setSupplierId] = useState('');
  const [adding, setAdding] = useState(false);
  // 370: the car whose bookings and lines are open (from its name in the header).
  const [openCar, setOpenCar] = useState<string | null>(null);

  const suppliers = useQuery({
    queryKey: ['sales-hosts'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/sales/hosts'),
    enabled: everyHost,
    staleTime: 300_000,
  });
  const ready = !everyHost || supplierId !== '';
  const query = new URLSearchParams({
    ...(everyHost && supplierId ? { supplier_id: supplierId } : {}),
    from: period.from,
    to: period.to,
  }).toString();
  const statement = useQuery({
    queryKey: ['sales-statement', query],
    queryFn: () => api.get<Statement>(`/sales/statement?${query}`),
    enabled: ready,
    retry: false,
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-statement'] });
  };
  const exportCsv = (s: Statement) => {
    const url = URL.createObjectURL(
      new Blob([statementToCsv(s)], { type: 'text/csv;charset=utf-8' }),
    );
    const a = document.createElement('a');
    const who = s.supplier.name || 'supplier';
    a.download = `sales-statement-${who}-${s.from}-${s.to}.csv`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };
  const choosePreset = (p: Preset) => {
    setPreset(p);
    setPeriod(presetRange(p));
  };
  const s = statement.data;
  const rows = s ? statementRows(s) : [];
  const headRow = rows.find((r) => r.section === 'head');
  const opened = s?.cars.find((c) => c.car_id === openCar) ?? null;

  return (
    <div>
      <PageHeader
        title="Sales Statement"
        description="What a supplier is owed: paid bookings by pickup date, adjustment lines, CARii commission and the monthly supplier cost, per car."
        actions={
          s ? (
            <div className="flex gap-2">
              {manages && s.cars.length > 0 ? (
                <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
                  + Add line
                </Button>
              ) : null}
              <Button type="button" onClick={() => exportCsv(s)}>
                Export CSV
              </Button>
            </div>
          ) : undefined
        }
      />
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          {everyHost && (
            <>
              <FormField label="Supplier" name="sales-host">
                <Select
                  id="sales-host"
                  className="min-w-48"
                  value={supplierId}
                  onChange={(e) => {
                    setSupplierId(e.target.value);
                    setOpenCar(null);
                  }}
                >
                  <option value="">Choose a supplier</option>
                  {(suppliers.data ?? []).map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <div aria-hidden="true" className="mx-1 hidden h-9 w-px bg-hairline sm:block" />
            </>
          )}
          <FormField label="From" name="sales-from">
            <Input
              id="sales-from"
              type="date"
              value={period.from}
              onChange={(e) => {
                setPreset(null);
                setPeriod({ ...period, from: e.target.value });
              }}
            />
          </FormField>
          <FormField label="To" name="sales-to">
            <Input
              id="sales-to"
              type="date"
              value={period.to}
              onChange={(e) => {
                setPreset(null);
                setPeriod({ ...period, to: e.target.value });
              }}
            />
          </FormField>
          <div className="flex overflow-hidden rounded-lg border border-hairline-strong text-sm">
            {PRESETS.map(([p, label]) => (
              <button
                key={p}
                type="button"
                aria-pressed={preset === p}
                onClick={() => choosePreset(p)}
                className={`border-r border-hairline px-3 py-2 last:border-r-0 ${
                  preset === p
                    ? 'bg-surface-feature font-semibold text-brand-navy shadow-[inset_0_-2px_0_var(--color-brand-blue)]'
                    : 'text-slate hover:bg-surface'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {s ? (
            <p data-testid="statement-summary" className="ml-auto self-center text-xs text-steel">
              {s.cars.length} {s.cars.length === 1 ? 'car' : 'cars'} · {daysBetween(s.from, s.to)}{' '}
              days
            </p>
          ) : null}
        </div>
      </Card>

      {!ready && <p className="mt-4 text-sm text-steel">Choose a supplier to see its statement.</p>}
      {ready && statement.isLoading && <Spinner />}
      {statement.isError && (
        <p className="mt-4 text-sm text-red-600">{errorText(statement.error)}</p>
      )}
      {s && headRow && (
        <>
          <Card>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink">
                {s.supplier.name} · {periodLabel(s.from, s.to)}
              </h2>
              <p className="text-sm text-steel">
                Net pay{' '}
                <span data-testid="net-pay" className="font-semibold text-ink">
                  {formatNet(s.totals.net_minor)}
                </span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table aria-label="Sales statement" className="w-full text-sm tabular-nums">
                <tbody>
                  <tr className="bg-surface">
                    <th scope="row" className="py-2 pr-4 text-left font-semibold text-ink">
                      {headRow.label}
                    </th>
                    {s.cars.map((c) => (
                      <td key={c.car_id} className="py-2 pr-4 text-right font-semibold">
                        <button
                          type="button"
                          aria-expanded={openCar === c.car_id}
                          onClick={() => setOpenCar(openCar === c.car_id ? null : c.car_id)}
                          className="text-ink hover:text-primary hover:underline"
                        >
                          {c.plate_number ?? c.label}
                        </button>
                      </td>
                    ))}
                    <td className="py-2 pr-4 text-right font-semibold text-ink">Total</td>
                  </tr>
                  {rows
                    .filter((r) => r.section !== 'head')
                    .map((r, i, list) => {
                      const startsSection = i === 0 || list[i - 1]?.section !== r.section;
                      const heading =
                        startsSection && r.section === 'income'
                          ? 'Income'
                          : startsSection && r.section === 'costs'
                            ? 'Costs'
                            : null;
                      const net = r.section === 'net';
                      return (
                        <Fragment key={r.label}>
                          {heading ? (
                            <tr>
                              <td
                                colSpan={s.cars.length + 2}
                                className="pt-4 pb-1 text-xs font-bold uppercase tracking-wide text-steel"
                              >
                                {heading}
                              </td>
                            </tr>
                          ) : null}
                          <tr
                            className={
                              net ? 'bg-brand-navy text-white' : 'border-b border-hairline-soft'
                            }
                          >
                            <th
                              scope="row"
                              className={`py-2 pr-4 text-left font-medium ${
                                net
                                  ? 'pl-2 text-white'
                                  : r.section === 'fee'
                                    ? 'text-steel'
                                    : 'text-slate'
                              }`}
                            >
                              {r.label}
                              {r.section === 'fee' ? (
                                <span className="ml-2 rounded-sm bg-surface px-1.5 py-0.5 text-[10px] font-bold text-steel">
                                  CARii’s, not in net pay
                                </span>
                              ) : null}
                            </th>
                            {r.cells.map((c, j) => {
                              const isTotal = j === r.cells.length - 1;
                              const zero = r.money && !isTotal && c === '0.00';
                              return (
                                <td
                                  // biome-ignore lint/suspicious/noArrayIndexKey: a column's position is its identity (car order, then Total)
                                  key={j}
                                  className={`py-2 pr-4 text-right ${
                                    net
                                      ? 'font-bold text-white'
                                      : zero
                                        ? 'text-steel/60'
                                        : r.section === 'fee'
                                          ? 'text-steel'
                                          : isTotal
                                            ? 'font-semibold text-ink'
                                            : 'text-ink'
                                  }`}
                                >
                                  {zero ? '—' : c}
                                </td>
                              );
                            })}
                          </tr>
                        </Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </Card>
          <div className="mt-4">
            <PaymentCard
              key={`${supplierId}-${s.from}-${s.to}-${s.payment?.at ?? ''}`}
              statement={s}
              supplierId={supplierId}
              period={period}
              manages={manages && everyHost}
              onSaved={refresh}
            />
          </div>
          {opened ? (
            <div className="mt-4">
              <CarLines car={opened} period={period} manages={manages} onChanged={refresh} />
            </div>
          ) : null}
          <p data-testid="statement-formula" className="mt-3 text-xs text-steel">
            Malaysia days. Revenue is the rental customers paid, CARii’s platform fee excluded;
            commission = rate × (revenue − self booking) + penalty commission (the penalty rate on
            each penalty); net pay = revenue + penalty − compensation − self booking − commission −
            supplier cost. Open a car’s bookings and lines from its name.
          </p>
          {manages && adding ? (
            <AddLineDialog
              open={adding}
              onClose={() => setAdding(false)}
              cars={s.cars}
              period={period}
              onAdded={refresh}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
